// API Request and Response Types

// Auth API Types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  organizationName?: string;
}

export interface VerifyEmailRequest {
  token: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface AuthResponse {
  user: {
    id: string;
    email: string;
    emailVerified: boolean;
    createdAt: string;
    organizations: Array<{
      id: string;
      name: string;
      slug: string;
      role: "owner" | "admin" | "member";
      createdBy: string;
      createdAt: string;
      updatedAt: string;
    }>;
  };
}

export interface MessageResponse {
  message: string;
}

// Organization API Types
export interface CreateOrganizationRequest {
  name: string;
  slug?: string;
}

export interface UpdateOrganizationRequest {
  name?: string;
  slug?: string;
}

export interface OrganizationResponse {
  id: string;
  name: string;
  slug: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface AddMemberRequest {
  email: string;
  role: "owner" | "admin" | "member";
}

export interface UpdateMemberRoleRequest {
  role: "owner" | "admin" | "member";
}

export interface MemberResponse {
  userId: string;
  email: string;
  emailVerified: boolean;
  role: "owner" | "admin" | "member";
  joinedAt: string;
}

// Team API Types
export interface CreateTeamRequest {
  name: string;
  description?: string;
}

export interface UpdateTeamRequest {
  name?: string;
  description?: string;
}

export interface TeamResponse {
  id: string;
  organizationId: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AddTeamMemberRequest {
  userId: string;
}

export interface TeamMemberResponse {
  userId: string;
  email: string;
  emailVerified: boolean;
  addedAt: string;
}

// Billing API Types
export type SubscriptionTier = "free" | "pro" | "enterprise" | "staff";
export type SubscriptionStatus = "active" | "past_due" | "canceled" | "incomplete";
export type BillingInterval = "monthly" | "yearly";

export interface TierLimits {
  maxSeats: number;
  maxTeams: number;
}

export interface BillingUsage {
  seats: number;
  teams: number;
  seatsPercent: number;
  teamsPercent: number;
  availableSeats: number;
  availableTeams: number;
}

export interface BillingStatus {
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  limits: TierLimits;
  usage: BillingUsage;
  stripeConfigured: boolean;
}

export interface CreateCheckoutRequest {
  interval: BillingInterval;
  successUrl: string;
  cancelUrl: string;
}

export interface CheckoutResponse {
  url: string;
}

export interface CreatePortalRequest {
  returnUrl: string;
}

export interface PortalResponse {
  url: string;
}

export interface InvoiceResponse {
  id: string;
  number: string | null;
  status: string | null;
  amount: number;
  currency: string;
  created: number;
  hostedInvoiceUrl: string | null;
  pdfUrl: string | null;
}

export interface InvoicesResponse {
  invoices: InvoiceResponse[];
}

// Permissions API Types
export interface OrganizationPermissions {
  /** Can invite new members to the organization */
  canInviteMember: boolean;
  /** Can create new teams within the organization */
  canCreateTeam: boolean;
  /** Can upgrade the subscription tier */
  canUpgrade: boolean;
  /** Can access and manage billing settings */
  canManageBilling: boolean;
  /** Can modify organization settings (name, slug) */
  canEditOrganization: boolean;
  /** Can delete the organization */
  canDeleteOrganization: boolean;
  /** Can manage member roles (promote/demote) */
  canManageMembers: boolean;
}

export interface OrganizationDetailResponse {
  organization: OrganizationResponse;
  permissions: OrganizationPermissions;
}

export interface BillingStatusResponse {
  billing: BillingStatus;
  permissions: OrganizationPermissions;
}

// API Key Types
export const API_KEY_SCOPES = ["read", "write", "admin"] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];

export interface ApiKeyResponse {
  id: string;
  organizationId: string;
  createdBy: string;
  name: string;
  keyPrefix: string;
  scopes: ApiKeyScope[];
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateApiKeyRequest {
  name: string;
  scopes: ApiKeyScope[];
}

export interface UpdateApiKeyRequest {
  name?: string;
  scopes?: ApiKeyScope[];
}

// Error Response Type
export interface ErrorResponse {
  error: string;
}

// Podcast API Types
export type RevocationPolicy = "immediate" | "end_of_period" | "grace_period";
export type EpisodeStatus = "draft" | "scheduled" | "published";
export type EpisodeType = "full" | "trailer" | "bonus";

export interface PodcastResponse {
  id: string;
  organizationId: string;
  name: string;
  slug: string;
  description: string | null;
  coverArtworkUrl: string | null;
  language: string;
  category: string | null;
  explicit: boolean;
  timezone: string;
  isPremium: boolean;
  revocationPolicy: RevocationPolicy;
  revocationGraceDays: number;
  applePodcastsId: string | null;
  spotifyId: string | null;
  youtubeMusicId: string | null;
  createdAt: string;
  updatedAt: string;
  episodeCount?: number;
}

export interface CreatePodcastRequest {
  name: string;
  slug?: string;
  description?: string;
  coverArtworkUrl?: string;
  language?: string;
  category?: string;
  explicit?: boolean;
  timezone?: string;
  isPremium?: boolean;
  revocationPolicy?: RevocationPolicy;
  revocationGraceDays?: number;
}

export interface UpdatePodcastRequest {
  name?: string;
  slug?: string;
  description?: string | null;
  coverArtworkUrl?: string | null;
  language?: string;
  category?: string | null;
  explicit?: boolean;
  timezone?: string;
  isPremium?: boolean;
  revocationPolicy?: RevocationPolicy;
  revocationGraceDays?: number;
  applePodcastsId?: string | null;
  spotifyId?: string | null;
  youtubeMusicId?: string | null;
}

export interface ChapterData {
  startTime: number;
  title: string;
}

export interface EpisodeResponse {
  id: string;
  podcastId: string;
  title: string;
  slug: string;
  description: string | null;
  showNotes: string | null;
  audioUrl: string | null;
  audioDurationSeconds: number | null;
  audioFileSizeBytes: number | null;
  transcriptUrl: string | null;
  status: EpisodeStatus;
  isPremium: boolean;
  seasonNumber: number | null;
  episodeNumber: number | null;
  episodeType: EpisodeType;
  explicit: boolean;
  chapters: ChapterData[] | null;
  publishedAt: string | null;
  scheduledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEpisodeRequest {
  title: string;
  slug?: string;
  description?: string;
  showNotes?: string;
  audioUrl?: string;
  audioDurationSeconds?: number;
  audioFileSizeBytes?: number;
  transcriptUrl?: string;
  status?: EpisodeStatus;
  isPremium?: boolean;
  seasonNumber?: number;
  episodeNumber?: number;
  episodeType?: EpisodeType;
  explicit?: boolean;
  chapters?: ChapterData[];
  scheduledAt?: string;
}

// Custom Domains
export interface CustomDomainResponse {
  id: string;
  podcastId: string;
  domain: string;
  verified: boolean;
  sslProvisioned: boolean;
  createdAt: string;
  verifiedAt: string | null;
  sslProvisionedAt: string | null;
}

export interface VerificationInstructions {
  domain: string;
  methods: {
    txt: { host: string; value: string };
    cname: { host: string; target: string };
  };
}

export interface DomainWithInstructions {
  domain: CustomDomainResponse;
  instructions: VerificationInstructions;
}

export interface VerificationResult {
  verified: boolean;
  method: string | null;
  message: string;
}

export interface UpdateEpisodeRequest {
  title?: string;
  slug?: string;
  description?: string | null;
  showNotes?: string | null;
  audioUrl?: string | null;
  audioDurationSeconds?: number | null;
  audioFileSizeBytes?: number | null;
  transcriptUrl?: string | null;
  status?: EpisodeStatus;
  isPremium?: boolean;
  seasonNumber?: number | null;
  episodeNumber?: number | null;
  episodeType?: EpisodeType;
  explicit?: boolean;
  chapters?: ChapterData[] | null;
  scheduledAt?: string | null;
}

// Audio Processing Types
export type AudioProcessingStepType =
  | "silence_removal"
  | "normalization"
  | "noise_reduction"
  | "transcription";

export type AudioProcessingStatus = "pending" | "processing" | "completed" | "failed";

export interface AudioProcessingStepResult {
  step: AudioProcessingStepType;
  status: "completed" | "failed" | "skipped";
  error?: string;
  durationMs?: number;
}

export interface AudioProcessingJobSummary {
  jobId: string;
  status: AudioProcessingStatus;
  steps: AudioProcessingStepType[];
  currentStepIndex: number;
  createdAt: string;
  completedAt: string | null;
}

export interface AudioProcessingJobDetail extends AudioProcessingJobSummary {
  episodeId: string;
  stepResults: AudioProcessingStepResult[];
  errorMessage: string | null;
  startedAt: string | null;
}

export interface StartProcessingResponse {
  jobId: string;
  status: AudioProcessingStatus;
  steps: AudioProcessingStepType[];
}

export interface ListProcessingJobsResponse {
  jobs: AudioProcessingJobSummary[];
}

// Transcript types
export interface TranscriptSegment {
  start: number;
  end: number;
  text: string;
  speaker?: string;
}

export interface Transcript {
  version: 1;
  language?: string;
  segments: TranscriptSegment[];
}

export interface TranscriptResponse {
  transcript: Transcript;
}

export interface TranscriptSaveResponse {
  transcript: Transcript;
  transcriptUrl: string;
}

export type MembershipType = "open" | "invite_only" | "application";

export interface NetworkResponse {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  logoUrl: string | null;
  membershipType: MembershipType;
  createdByOrgId: string;
  createdAt: string;
  updatedAt: string;
}

// Network Tier Types
export interface NetworkTierResponse {
  id: string;
  networkId: string;
  name: string;
  description: string | null;
  monthlyPriceCents: number;
  annualPriceCents: number;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  includedTiers?: NetworkTierResponse[];
}

export interface CreateNetworkTierRequest {
  name: string;
  description?: string;
  monthlyPriceCents: number;
  annualPriceCents: number;
  sortOrder?: number;
}

export interface UpdateNetworkTierRequest {
  name?: string;
  description?: string | null;
  monthlyPriceCents?: number;
  annualPriceCents?: number;
}

export interface ReorderTiersRequest {
  tierIds: string[];
}

// Network Membership Types
export type MembershipStatus = "pending" | "active" | "departed";

export interface NetworkMembershipResponse {
  id: string;
  networkId: string;
  podcastId: string;
  podcastName: string;
  tierId: string | null;
  status: MembershipStatus;
  revenueSplitPercent: number;
  joinedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InvitePodcastRequest {
  podcastId: string;
  tierId?: string;
  revenueSplitPercent?: number;
}

export interface UpdateMembershipRequest {
  tierId?: string;
  revenueSplitPercent?: number;
}

export interface CreateNetworkRequest {
  name: string;
  slug: string;
  description?: string;
  logoUrl?: string;
  membershipType?: MembershipType;
}

export interface UpdateNetworkRequest {
  name?: string;
  slug?: string;
  description?: string | null;
  logoUrl?: string | null;
  membershipType?: MembershipType;
}
